import { trainerRoleLabel } from './trainerUtils';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FileText, Download,
} from 'lucide-react';
import { formatDate } from '../../utils/dateFormat';
import WorkflowRefLink from '../shared/WorkflowRefLink';
import { fetchJSON, API_BASE_URL } from '../../utils/api';
import { fmtBytes } from '../workflow/constants';
import FileList from '../shared/FileList';
import { DateChip, MetaLine, fmtHours } from './SessionRow';

/**
 * Read-only lists of ONE person's training record — attendance, absences,
 * qualifications and External CPD.
 *
 * These live in components/lnd (not in the My Records page) so any surface
 * showing "what this person has done" renders the same rows: change what a
 * qualification says here and every reader of it follows.
 * Rows are built from the ldRepository.staffJourney envelope, which is what
 * both /api/my/training and the HR Staff Journey tab return.
 */

const EmptyLine = ({ children }) => (
  <p className="text-sm text-slate-500 px-5 py-6 text-center">{children}</p>
);

// ── Attendance (sessions I sat in, or taught) ──────────────────────────────

export const AttendanceRow = ({ record, today, highlighted }) => {
  const hours = record.isTrainer ? record.trainerHours : record.trainingHours;
  return (
    <div className={`flex items-start gap-3 px-5 py-3 ${highlighted ? 'bg-violet-50/60 ring-1 ring-inset ring-violet-200' : ''}`}>
      <DateChip date={record.date} today={today} tone={record.isTrainer ? 'emerald' : 'slate'} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-medium text-slate-800 truncate">{record.courseTitle || record.sessionCode}</p>
          {record.isTrainer && (
            <span className="shrink-0 px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[11px] font-semibold">
              {trainerRoleLabel(record.trainerRole)}
            </span>
          )}
          {!record.isTrainer && record.isMandatory && (
            <span className="shrink-0 px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-[11px] font-semibold">
              Mandatory
            </span>
          )}
          {/* Credited by passing the quiz without attending — the hours
              count, the presence did not happen (docs/modules/ld.md §5.7). */}
          {record.creditSource === 'quiz' && (
            <span
              className="shrink-0 px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 text-[11px] font-semibold"
              title="Did not attend; passed the post-course quiz, so the session's hours are credited"
            >
              Credited via quiz
            </span>
          )}
        </div>
        <MetaLine items={[
          record.startEndTime && { text: record.startEndTime },
          !record.isTrainer && { text: record.trainerNames || 'Trainer not recorded', muted: !record.trainerNames },
          { text: record.venueName || 'Venue not specified', muted: !record.venueName },
          record.categoryTitle && { text: record.categoryTitle },
        ]} />
        {/* Only /api/my/training sets it — same rule as the Overview's Give Feedback row. */}
        {record.feedbackPending && (
          <MetaLine className="mt-0.5" items={[{ text: 'Feedback not submitted yet', warn: true }]} />
        )}
      </div>
      <span className="shrink-0 mt-0.5 px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 text-xs font-medium tabular-nums">
        {fmtHours(hours || 0)} h
      </span>
    </div>
  );
};

// `rowTo(record)` (optional) makes each row a link — My Records points every
// session at its page; the HR Staff Journey tab leaves rows plain.
const linkRow = (rowTo, record, key, body) => (rowTo ? (
  <Link key={key} to={rowTo(record)} className="block hover:bg-slate-50 transition-colors">{body}</Link>
) : <div key={key}>{body}</div>);

export const AttendanceList = ({ records = [], today, highlightId, rowTo = null }) => {
  if (records.length === 0) return <EmptyLine>No attendance recorded yet.</EmptyLine>;
  return (
    <div className="divide-y divide-slate-100">
      {records.map((r) => linkRow(rowTo, r, r.id, (
        <AttendanceRow record={r} today={today} highlighted={highlightId === r.id} />
      )))}
    </div>
  );
};

// ── Absences (approved, not voided) ────────────────────────────────────────

export const AbsenceList = ({ records = [], today, rowTo = null }) => {
  if (records.length === 0) return <EmptyLine>No absences on record.</EmptyLine>;
  return (
    <div className="divide-y divide-slate-100">
      {records.map((a, i) => linkRow(rowTo, a, `${a.sessionCode}-${a.date}-${i}`, (
        <div className="flex items-start gap-3 px-5 py-3">
          <DateChip date={a.date} today={today} tone="amber" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-slate-800 truncate">{a.courseTitle || a.sessionCode}</p>
            <p className="mt-1 text-xs text-slate-500">
              {[a.reasonCategory, a.reasonDetail].filter(Boolean).join(' — ') || 'Reason not recorded'}
            </p>
          </div>
          {a.evidence && (
            <span className="shrink-0 mt-0.5 px-2 py-0.5 rounded-md bg-slate-100 text-slate-500 text-xs">
              Evidence on file
            </span>
          )}
        </div>
      )))}
    </div>
  );
};

// ── Certificates on a qualification / CPD record ───────────────────────────
// Both records keep their certificates behind an authenticated GET; the cookie
// session authenticates it, so a plain anchor is all a download needs. Serving
// them to the record's OWNER (not just ld:view) is what makes My Records
// useful — see the ldViewOrOwner guard in lnd.routes.js.

const CertificateLinks = ({ path, count }) => {
  const [files, setFiles] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open || files || error) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchJSON(path);
        if (!cancelled) setFiles(data.data || []);
      } catch (e) { if (!cancelled) setError(e.message); }
    })();
    return () => { cancelled = true; };
  }, [open, path, files, error]);

  if (!count) return null;
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
      >
        <FileText className="w-3.5 h-3.5" />
        {count} certificate{count === 1 ? '' : 's'}
      </button>
    );
  }
  return (
    <div className="mt-1 space-y-1">
      {error && (
        <p className="text-xs text-red-600">{error}</p>
      )}
      {!error && files === null && <p className="text-xs text-slate-500">Loading…</p>}
      <FileList
        files={files || []}
        divided={false}
        className="space-y-1"
        row={(f) => ({
          name: f.originalName,
          href: `${API_BASE_URL}${path}/${f.id}`,
          icon: Download,
          dense: true,
          pad: '',
          meta: [f.sizeBytes ? fmtBytes(f.sizeBytes) : null],
        })}
      />
    </div>
  );
};

// ── Qualifications held ────────────────────────────────────────────────────

/**
 * @param {'list'|'chips'} variant  'chips' is the compact profile-card cluster
 *   (Staff Journey); 'list' is the full reading row (My Records). Same fields,
 *   two densities — so adding one only means deciding where it goes.
 */
export const QualificationList = ({ records = [], variant = 'list', basePath = '/api/ld/qualifications' }) => {
  if (records.length === 0) {
    return variant === 'chips'
      ? <p className="text-sm text-slate-500">None on record</p>
      : <EmptyLine>No qualifications on record.</EmptyLine>;
  }

  if (variant === 'chips') {
    return (
      <div className="flex flex-wrap gap-2">
        {records.map((q) => (
          <span
            key={q.id}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200 text-xs text-amber-800"
            title={[q.fullName, q.awardingBody].filter(Boolean).join(' — ')}
          >
            {q.title}
            {q.awardedDate && <span className="text-amber-600">· {formatDate(q.awardedDate)}</span>}
            {q.expiryDate && <span className="text-amber-600">· exp. {formatDate(q.expiryDate)}</span>}
          </span>
        ))}
      </div>
    );
  }

  return (
    <div className="divide-y divide-slate-100">
      {records.map((q) => (
        <div key={q.id} className="flex items-start gap-3 px-5 py-3">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-slate-800">
              {q.title}
              {q.fullName && <span className="text-slate-500 font-normal"> — {q.fullName}</span>}
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
              {q.awardingBody && <span>{q.awardingBody}</span>}
              <span>Awarded {q.awardedDate ? formatDate(q.awardedDate) : '—'}</span>
              {q.expiryDate && <span>Expires {formatDate(q.expiryDate)}</span>}
              {q.requiresRenewal && !q.expiryDate && <span className="text-amber-700">Renewable — no expiry recorded</span>}
              {/* 產生這筆資格的申報單——點得過去。 */}
              {q.sourceRequestNo && <WorkflowRefLink refNo={q.sourceRequestNo} />}
            </div>
            {q.remarks && <p className="mt-1 text-xs text-slate-500">{q.remarks}</p>}
            <div className="mt-1">
              <CertificateLinks path={`${basePath}/${q.id}/files`} count={q.fileCount} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ── External CPD ───────────────────────────────────────────────────────────

// `onWithdraw(record)` — My Records only: a record filed by the viewer's own
// declaration can be withdrawn until HR has confirmed a flag on it
// (`withdrawable` rides on the row; the server has the final say).
export const CpdList = ({ records = [], basePath = '/api/ld/cpd-records', onWithdraw = null }) => {
  if (records.length === 0) return <EmptyLine>No External CPD recorded yet.</EmptyLine>;
  return (
    <div className="divide-y divide-slate-100">
      {records.map((r) => (
        <div key={r.id} className="flex items-start gap-3 px-5 py-3">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-slate-800">{r.trainingTitle}</p>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
              <span>Certificate dated {formatDate(r.certificateDate)}</span>
              {r.categoryTitle && <span>{r.categoryTitle}</span>}
              {r.requestId && (
                <WorkflowRefLink to={`/workflow/cpd/${r.requestId}`}>{r.requestNo || `CPD-${r.requestId}`}</WorkflowRefLink>
              )}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-3">
              <CertificateLinks path={`${basePath}/${r.id}/files`} count={r.fileCount} />
              {onWithdraw && r.withdrawable && (
                <button
                  type="button"
                  onClick={() => onWithdraw(r)}
                  className="text-xs text-red-600 hover:text-red-800 font-medium"
                >
                  Withdraw
                </button>
              )}
            </div>
          </div>
          <span className="shrink-0 mt-0.5 px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 text-xs font-medium tabular-nums">
            {fmtHours(r.hours || 0)} h
          </span>
        </div>
      ))}
    </div>
  );
};
